import { motion } from 'framer-motion';

interface AreaProps {
    title: string;
    body: string;
    delay: number;
}

const areas: AreaProps[] = [
    {
        title: 'Calibrated forecasting',
        body: 'Can systems learn to say "I don\'t know" at the right times? We\'re testing multi-model ensembles where disagreement between models is the uncertainty signal.',
        delay: 0.1,
    },
    {
        title: 'Earned confidence',
        body: 'Instead of asserting confidence, what if systems tracked their own accuracy and showed you their track record? We\'re building evaluation frameworks that score every recommendation against what actually happened.',
        delay: 0.2,
    },
    {
        title: 'Compounding memory',
        body: 'Most AI resets between conversations. We\'re researching memory architectures where past outcomes inform future decisions — so the system gets smarter from experience, not just training data.',
        delay: 0.3,
    },
    {
        title: 'Convergence',
        body: 'When these three ideas work together, something interesting happens. Honest uncertainty + tracked outcomes + persistent memory creates a feedback loop. Each decision makes the next one better.',
        delay: 0.4,
    },
];

export const Exploring = () => {
    return (
        <section className="relative py-24 md:py-32 px-6 md:px-24">
            <div className="max-w-2xl mx-auto">
                {/* Eyebrow */}
                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="text-[10px] uppercase tracking-[0.4em] text-gray-600 font-mono mb-4"
                >
                    Research areas
                </motion.p>

                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="text-base text-gray-500 font-light mb-14"
                >
                    We're a small lab exploring a few ideas we find genuinely interesting:
                </motion.p>

                {/* Research areas */}
                <div className="space-y-10">
                    {areas.map(({ title, body, delay }) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, delay }}
                            className="group"
                        >
                            <h3 className="text-base text-white font-normal mb-2">
                                {title}
                            </h3>
                            <p className="text-sm text-gray-500 font-light leading-relaxed">
                                {body}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* Subtle divider */}
                <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.5 }}
                    className="h-[1px] bg-gradient-to-r from-transparent via-gray-800 to-transparent mt-20 origin-left"
                />
            </div>
        </section>
    );
};
